import { ComponentType, FunctionComponent, MouseEvent, useCallback, useMemo, useState } from 'react';

import { faPen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { process } from '@progress/kendo-data-query';
import { GridCellProps, GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import styled from 'styled-components';

import { WorklistViewModel } from 'models';

import { useEvent } from 'core/hooks';
import {
  Action,
  ActionListCell,
  DEFAULT_DATA_TABLE_DATA_STATE,
  DEFAULT_PAGE_SIZES,
  DataTable,
  HeaderCell,
  Page,
  PageHeader,
  SwitchCell,
  TextCell,
  Toolbar,
} from 'core/ui';

import { WorklistGridViewerProps } from '../types/WorklistGridViewerProps';

const PAGEABLE_SETTINGS = { pageSizes: DEFAULT_PAGE_SIZES };

export const WorklistGridViewer: FunctionComponent<WorklistGridViewerProps> = ({
  worklists,
  isLoading = false,
  onEditWorklist,
  onDeleteWorklist,
  onRefresh,
}) => {
  // Add state management for data table state (filters, sorting, pagination)
  const [dataState, setDataState] = useState(DEFAULT_DATA_TABLE_DATA_STATE);

  // Add data state change handler for filter inputs
  const handleDataStateChange = useCallback((changeEvent: GridDataStateChangeEvent) => {
    setDataState(changeEvent.dataState);
  }, []);

  const handleEditClick = useEvent((event: MouseEvent<HTMLButtonElement>, dataItem: WorklistViewModel) => {
    onEditWorklist(dataItem);
  });

  const handleDeleteClick = useEvent((event: MouseEvent<HTMLButtonElement>, dataItem: WorklistViewModel) => {
    onDeleteWorklist?.(dataItem);
  });

  const gridActions = useMemo((): Action[] => {
    const actions: Action[] = [
      {
        key: 'edit',
        text: 'Edit',
        icon: faPen,
        onClick: handleEditClick,
      },
    ];

    if (onDeleteWorklist) {
      actions.push({
        key: 'delete',
        text: 'Delete',
        icon: faTrash,
        onClick: handleDeleteClick,
      });
    }

    return actions;
  }, [handleEditClick, handleDeleteClick, onDeleteWorklist]);

  // Transform worklists data to include computed fields
  const worklistsData = useMemo(() => {
    return worklists.map((worklist) => ({
      ...worklist,
      // Add computed fields for display
      active: true, // Default to true since active field is not in the model
      dateCreated: new Date().toISOString(), // Placeholder - replace with actual date if available
      ordinal: worklist.ordinal ?? 0, // Ensure ordinal is always a number
    }));
  }, [worklists]);

  // Process data with client-side filtering, sorting, and pagination
  const dataResult = useMemo(() => {
    const result = process(worklistsData, dataState);
    return {
      data: result.data,
      total: result.total,
    };
  }, [worklistsData, dataState]);

  const ActiveCell = (props: GridCellProps) => (
    <td>
      <SwitchCell {...props} field="active" />
    </td>
  );

  const DateCell = ({ dataItem }: GridCellProps) => <td>{dataItem.dateCreated ? new Date(dataItem.dateCreated).toLocaleDateString() : '-'}</td>;

  return (
    <Page>
      <PageHeader title="Worklist Management" />
      <StyledDataTable
        data={dataResult}
        total={dataResult.total}
        pageable={PAGEABLE_SETTINGS}
        filterable
        sortable
        reorderable
        isLoading={isLoading}
        actions={gridActions}
        {...dataState}
        onDataStateChange={handleDataStateChange}
      >
        <Toolbar onRefreshClick={onRefresh} />
        <GridColumn
          title="Action"
          headerCell={HeaderCell}
          cell={ActionListCell as ComponentType<GridCellProps>}
          width="80px"
          filterable={false}
          sortable={false}
        />
        <GridColumn title="Ordinal" field="ordinal" headerCell={HeaderCell} cell={TextCell} width="100px" filter="numeric" />
        <GridColumn title="Worklist Name" field="name" headerCell={HeaderCell} cell={TextCell} width="300px" filter="text" />
        <GridColumn title="Entity" field="entity" headerCell={HeaderCell} cell={TextCell} width="200px" filter="text" />
        <GridColumn title="Created User" field="user" headerCell={HeaderCell} cell={TextCell} width="200px" filter="text" />
        <GridColumn title="Date Created" field="dateCreated" headerCell={HeaderCell} cell={DateCell} width="150px" filter="date" />
        <GridColumn title="Active" field="active" headerCell={HeaderCell} cell={ActiveCell} width="100px" filter="boolean" />
      </StyledDataTable>
    </Page>
  );
};

const StyledDataTable = styled(DataTable)`
  .k-grid-header {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  .k-header {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;
